import React, { useState, useEffect } from "react";
import "./CurrentMonthCalendar.css";
import { getCalendar } from "../../../api/api";
import { useNavigate } from "react-router-dom";

const months = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const formatDateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CurrentMonthCalendar = () => {
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currentDate = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    const loadMonthData = async () => {
      try {
        setLoading(true);
        const firstDay = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const lastDay = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );

        const startDate = formatDateToString(firstDay);
        const endDate = formatDateToString(lastDay);

        const data = await getCalendar(startDate, endDate);
        const calendarDataMap = {};
        data.forEach((day) => {
          calendarDataMap[day.date] = day;
        });
        setCalendarData(calendarDataMap);
      } catch (error) {
        console.error("Ошибка при загрузке календаря:", error);
      } finally {
        setLoading(false);
      }
    };

    loadMonthData();
  }, []);

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Определяем день недели для первого дня месяца (0 - воскресенье, 1 - понедельник ...)
    let firstDayOfWeek = firstDay.getDay();
    // Сдвигаем: если воскресенье (0), то это 7-й столбец
    firstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;

    // Добавляем дни предыдущего месяца
    const prevMonthLastDay = new Date(year, month, 0);
    const daysFromPrevMonth = firstDayOfWeek - 1;

    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const prevDate = new Date(
        year,
        month - 1,
        prevMonthLastDay.getDate() - i
      );
      const dateString = formatDateToString(prevDate);
      const dayData = calendarData[dateString] || {};
      const dayOfWeek = prevDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      days.push({
        date: prevMonthLastDay.getDate() - i,
        isOtherMonth: true,
        isWeekend,
        isHoliday: !dayData.workday_flag && dayData.event_name,
        isWorkday: dayData.workday_flag,
        event: dayData.event_name,
      });
    }

    // Добавляем дни текущего месяца
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const currentDateObj = new Date(year, month, i);
      const dateString = formatDateToString(currentDateObj);
      const dayData = calendarData[dateString] || {};
      const dayOfWeek = currentDateObj.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      days.push({
        date: i,
        isOtherMonth: false,
        isWeekend,
        isHoliday: !dayData.workday_flag && dayData.event_name,
        isWorkday: dayData.workday_flag,
        event: dayData.event_name,
      });
    }

    // Добавляем дни следующего месяца
    const lastDayOfWeek = days.length % 7 || 7;
    const remainingDays = lastDayOfWeek < 7 ? 7 - lastDayOfWeek : 0;

    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      const dateString = formatDateToString(nextDate);
      const dayData = calendarData[dateString] || {};
      const dayOfWeek = nextDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      days.push({
        date: i,
        isOtherMonth: true,
        isWeekend,
        isHoliday: !dayData.workday_flag && dayData.event_name,
        isWorkday: dayData.workday_flag,
        event: dayData.event_name,
      });
    }

    return days;
  };

  const handleFullCalendarClick = () => {
    navigate("/calendar-v2");
  };

  return (
    <div className="current-month-calendar">
      <div className="current-month-calendar__grid">
        <div className="current-month-calendar__weekdays">
          <div>Пн</div>
          <div>Вт</div>
          <div>Ср</div>
          <div>Чт</div>
          <div>Пт</div>
          <div>Сб</div>
          <div>Вс</div>
        </div>
        <div className="current-month-calendar__days">
          {loading ? (
            <div className="current-month-calendar__loading">Загрузка...</div>
          ) : (
            getDaysInMonth().map((day, index) => {
              const isCurrentDay =
                !day.isOtherMonth &&
                day.date === today.getDate() &&
                currentDate.getMonth() === today.getMonth() &&
                currentDate.getFullYear() === today.getFullYear();
              return (
                <div
                  key={index}
                  className={`current-month-calendar__day ${
                    day.isOtherMonth ? "other-month" : ""
                  } ${day.isWeekend ? "weekend" : ""} ${
                    day.isHoliday ? "holiday" : ""
                  } ${isCurrentDay ? "current-day" : ""} ${
                    day.event ? "has-event" : ""
                  }`}
                >
                  <div className="current-month-calendar__day-number">
                    {day.date}
                  </div>
                  {!day.isOtherMonth && day.event && (
                    <>
                      <div className="current-month-calendar__event-indicator" />
                      <div className="current-month-calendar__tooltip">
                        {day.event}
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      <button
        className="current-month-calendar__full-calendar-button"
        onClick={handleFullCalendarClick}
      >
        Перейти к полному календарю
      </button>
    </div>
  );
};

export default CurrentMonthCalendar;
